<template>
    <div>
        <div class="main-inner" v-if="project.name">
            <div class="row align-items-center mb-3">
                <div class="col-12 col-md-12 col-xl-8">
                    <h1 class="mb-0">Project: {{ project.name }}</h1>
                </div>
            </div>

            <div class="row">
                <div class="col-12 col-xl-4">
                    <Slider :project="project"></Slider>

                    <div class="block">
                        <Details :project="project"></Details>

                        <ul class="dotted-dropdown d-flex">
                            <li>
                                <router-link :to="{ name: 'editProject', params: { id: project.id } }" class="btn btn-primary mt-3 mr-2" v-permission:manage_projects="currentUser">Bewerken</router-link>
                            </li>

                            <li v-permission:manage_objects="currentUser" v-if="this.project.status === 0"><a href="#" @click.prevent="toggleImportObjects" class="btn btn-info mt-3">Objecten importeren</a></li>

                            <li v-role:opdrachtgever="currentUser" v-if="project.reports.length > 0"><a @click.prevent="toggleReportsPopup" href="#" class="btn btn-primary mt-3 mr-3">Documenten</a></li>

                            <li v-role:opdrachtgever="currentUser">
                                <router-link :to="{ name: 'onlineProjectDossier', params: { id: project.id } }" target="_blank"  class="btn btn-info mt-3">Online dossier</router-link>
                            </li>

                            <li class="align-self-end ml-auto" v-permission:manage_projects="currentUser">
                                <a href="#" @click.prevent>
                                    <img src="/img/icon-dots.svg" alt="" width="45" height="45">
                                </a>

                                <ul>
                                    <li><a href="#" v-permission:manage_projects="currentUser" @click.prevent="triggerExportObjects">Exporteren</a></li>
                                    <li v-if="project.reports.length > 0"><a href="#" @click.prevent="toggleReportsPopup">Documenten</a></li>
                                    <li v-permission:manage_projects="currentUser" @click.prevent="toggleGenerateReportPopup"><a href="#">Rapport genereren</a></li>
                                    <li>
                                        <router-link :to="{ name: 'onlineProjectDossier', params: { id: project.id } }" target="_blank">Online dossier</router-link>
                                    </li>
                                    <li v-permission:manage_projects="currentUser" v-if="project.status === 0"><a href="#" @click.prevent="closeProject">Project afronden</a></li>
                                </ul>
                            </li>
                        </ul>
                    </div>

                    <div class="block mt mb">
                        <h3>Opdrachtgever</h3>
                        <p>Contactpersonen bij deze opdrachtgever.</p>

                        <ul class="block__users">
                            <li v-for="user in project.company.users" :key="user.id">
                                <h4>{{ user.name }}</h4>
                                <span class="d-block">E: <a :href="'mailto:' + user.email">{{ user.email }}</a></span>
                                <span class="d-block" v-if="user.phone">T: <a :href="'tel:' + user.phone">{{ user.phone }}</a></span>
                            </li>
                        </ul>
                    </div>

                    <Reports v-if="reportsPopup" @togglePopup="toggleReportsPopup" :reports="project.reports" :project="project"></Reports>
                    <GenerateReport v-if="generateReportPopup" @togglePopup="toggleGenerateReportPopup" :project="project"></GenerateReport>
                    <AddObject v-if="addObject" @togglePopup="toggleAddObject" @objectAdded="objectAdded" :project="project.id" :startDate="project.startDate"></AddObject>
                    <ImportObjects v-if="importObjects" @togglePopup="toggleImportObjects" :project="project.id"></ImportObjects>
                </div>

                <div class="col-12 col-xl-8">
                    <div class="row align-items-center mb-3">
                        <div class="col-6">
                            <h2 class="mb-0">Objecten</h2>
                        </div>

                        <div class="col-6 text-right" v-if="project.status === 0">
                            <a href="#" class="btn btn-primary mb-0" @click.prevent="toggleAddObject" v-permission:add_objects="currentUser">Object toevoegen</a>
                        </div>
                    </div>

                    <div class="objects" :class="{'is-loading': objectsLoading}">
                        <Object v-for="object in objects.data.data" :key="object.id" :object="object" :project="project"></Object>
                    </div>

                    <Pagination :pagination="objects.meta" @paginate="changePage" :offset="4" :arrows="false"></Pagination>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import {SET_LOADING} from '@/store/modules/loader'
    import {FETCH_PROJECT} from '@/store/modules/projects'
    import {FETCH_OBJECTS} from '@/store/modules/objects'
    import {mapGetters} from 'vuex'
    import AddObject from '@/components/objects/AddObject'
    import ImportObjects from '@/components/objects/ImportObjects'
    import Pagination from '@/components/global/Pagination'
    import ProjectStatus from '@/utilities/mapping/ProjectStatus'
    import Object from '@/components/objects/Object'
    import Details from '@/components/projects/Details'
    import Slider from '@/components/projects/Slider'
    import Reports from '@/components/projects/Reports'
    import GenerateReport from '@/components/projects/GenerateReport'
    import {UPDATE_PROJECT} from '../../store/modules/projects'

    export default {
        components: {Slider, Details, Object, AddObject, ImportObjects, Pagination, Reports, GenerateReport},

        mounted() {
            this.fetchData()
            this.fetchObjects()
        },

        data() {
            return {
                objectsLoading: true,
                addObject: false,
                importObjects: false,
                reportsPopup: false,
                generateReportPopup: false,
                project: {},
            }
        },

        computed: {
            ...mapGetters([
                'currentUser',
                'objects',
                'counters',
            ]),
        },

        methods: {
            closeProject() {
                let confirmation = confirm('Weet je zeker dat je dit project wilt afronden?')

                if (confirmation) {
                    this.$store.dispatch(UPDATE_PROJECT, {
                        id: this.project.id,
                        changes: {
                            status: 1,
                        },
                    }).then(() => {
                        window.location.reload()
                    })
                }
            },

            toggleGenerateReportPopup() {
                this.generateReportPopup = !this.generateReportPopup
            },

            toggleReportsPopup() {
                this.reportsPopup = !this.reportsPopup
            },

            fetchData() {
                this.$store.dispatch(SET_LOADING, true)

                this.$store.dispatch(FETCH_PROJECT, this.$route.params.id).then((data) => {
                    this.project = data
                    this.$store.dispatch(SET_LOADING, false)
                })
            },

            triggerExportObjects() {
                window.axios({
                    url: process.env.VUE_APP_API_URL + '/objects/' + this.project.id + '/export',
                    method: 'GET',
                    responseType: 'blob', // important
                }).then((response) => {
                    const url = window.URL.createObjectURL(new Blob([response.data]))
                    const link = document.createElement('a')

                    link.href = url

                    link.setAttribute('download', this.project.file_id + ' - ' + this.project.name + '.xlsx')

                    document.body.appendChild(link)
                    link.click()
                })
            },

            fetchObjects(page = 1) {
                this.objectsLoading = true

                this.$store.dispatch(FETCH_OBJECTS, {
                    project: this.$route.params.id,
                    page: page,
                }).then(() => {
                    this.objectsLoading = false
                })
            },

            toggleAddObject() {
                this.addObject = !this.addObject
            },

            toggleImportObjects() {
                this.importObjects = !this.importObjects
            },

            objectAdded() {
                this.addObject = false
            },

            changePage() {
                this.fetchObjects(this.objects.meta.current_page)
            },
        },

        filters: {
            formatCounters(value) {
                return value.map((counter) => {
                    return ProjectStatus[counter.status] + ': ' + counter.amount
                }).join(', ')
            },
        },
    }
</script>